import { useState } from 'react'
import { useQuery, usePaginatedQuery } from 'convex/react'
import { Link } from 'react-router-dom'
import { Loader2, Search, Building2, User, ExternalLink } from 'lucide-react'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'

type Tab = 'people' | 'funds'

function ChipRow({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string
  options: string[]
  selected: string[]
  onToggle: (v: string) => void
}) {
  if (!options.length) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-wide text-neutral-500 mr-1">{label}</span>
      {options.map((opt) => {
        const active = selected.includes(opt)
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={cn(
              'px-2 py-0.5 text-xs rounded-full border transition-colors',
              active
                ? 'bg-neutral-900 text-white border-neutral-900'
                : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500',
            )}
          >
            {opt}
          </button>
        )
      })}
    </div>
  )
}

function PeopleTab({ search }: { search: string }) {
  const [sectors, setSectors] = useState<string[]>([])
  const [stages, setStages] = useState<string[]>([])
  const [geo, setGeo] = useState<string[]>([])
  const [leadsOnly, setLeadsOnly] = useState(false)

  const facets = useQuery(api.vcdb.investorFacets, {})
  const { results, status, loadMore } = usePaginatedQuery(
    api.vcdb.listInvestors,
    {
      search: search || undefined,
      sectors: sectors.length ? sectors : undefined,
      stages: stages.length ? stages : undefined,
      geo: geo.length ? geo : undefined,
      leadsOnly: leadsOnly || undefined,
    },
    { initialNumItems: 60 },
  )

  const toggle = (setter: typeof setSectors, current: string[]) => (v: string) => {
    setter(current.includes(v) ? current.filter((x) => x !== v) : [...current, v])
  }

  if (status === 'LoadingFirstPage' || !facets) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <ChipRow label="Sectors" options={facets.sectors} selected={sectors} onToggle={toggle(setSectors, sectors)} />
        <ChipRow label="Stages" options={facets.stages} selected={stages} onToggle={toggle(setStages, stages)} />
        <ChipRow label="Geo" options={facets.geo} selected={geo} onToggle={toggle(setGeo, geo)} />
        <label className="inline-flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
          <input
            type="checkbox"
            checked={leadsOnly}
            onChange={(e) => setLeadsOnly(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-neutral-300"
          />
          Leads rounds only
        </label>
      </div>

      <div className="text-xs text-neutral-500">
        Showing {results.length} investor{results.length === 1 ? '' : 's'}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {results.map((inv) => (
          <Link
            key={inv.slug}
            to={`/admin/investors/${inv.slug}`}
            className="group flex flex-col gap-2 rounded-lg border border-neutral-200 bg-white p-4 hover:border-neutral-400 hover:shadow-sm transition-all"
          >
            <div className="flex items-start gap-3">
              {inv.headshotUrl ? (
                <img
                  src={inv.headshotUrl}
                  alt={inv.name}
                  className="h-12 w-12 rounded-full object-cover bg-neutral-100"
                  loading="lazy"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-neutral-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-neutral-400" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-neutral-900 truncate group-hover:text-neutral-700">
                  {inv.name}
                </div>
                <div className="text-xs text-neutral-500 truncate">{inv.title ?? '—'}</div>
              </div>
            </div>

            <div className="text-xs text-neutral-600">
              {inv.avgCheckText && <span className="font-medium">{inv.avgCheckText}</span>}
              {inv.avgCheckText && inv.stages.length > 0 && <span className="text-neutral-400"> · </span>}
              {inv.stages.length > 0 && <span>{inv.stages.join(', ')}</span>}
            </div>

            {inv.sectors.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {inv.sectors.slice(0, 3).map((s) => (
                  <span
                    key={s}
                    className="px-1.5 py-0.5 text-[10px] rounded bg-neutral-100 text-neutral-600"
                  >
                    {s}
                  </span>
                ))}
                {inv.sectors.length > 3 && (
                  <span className="px-1.5 py-0.5 text-[10px] text-neutral-400">
                    +{inv.sectors.length - 3}
                  </span>
                )}
              </div>
            )}

            {inv.leadsRounds && (
              <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">Leads rounds</div>
            )}
          </Link>
        ))}
      </div>

      {results.length === 0 && status !== 'LoadingMore' && (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-500">
          No investors match the current filters.
        </div>
      )}

      {status === 'CanLoadMore' && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={() => loadMore(60)}
            className="text-xs px-4 py-2 rounded-md border border-neutral-300 hover:border-neutral-500 text-neutral-700"
          >
            Load more
          </button>
        </div>
      )}
      {status === 'LoadingMore' && (
        <div className="flex justify-center pt-2">
          <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
        </div>
      )}
    </div>
  )
}

function FundsTab({ search }: { search: string }) {
  const [sectors, setSectors] = useState<string[]>([])
  const [stages, setStages] = useState<string[]>([])
  const [geo, setGeo] = useState<string[]>([])

  const facets = useQuery(api.vcdb.fundFacets, {})
  const { results, status, loadMore } = usePaginatedQuery(
    api.vcdb.listFunds,
    {
      search: search || undefined,
      sectors: sectors.length ? sectors : undefined,
      stages: stages.length ? stages : undefined,
      geo: geo.length ? geo : undefined,
    },
    { initialNumItems: 60 },
  )

  const toggle = (setter: typeof setSectors, current: string[]) => (v: string) => {
    setter(current.includes(v) ? current.filter((x) => x !== v) : [...current, v])
  }

  if (status === 'LoadingFirstPage' || !facets) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <ChipRow label="Sectors" options={facets.sectors} selected={sectors} onToggle={toggle(setSectors, sectors)} />
        <ChipRow label="Stages" options={facets.stages} selected={stages} onToggle={toggle(setStages, stages)} />
        <ChipRow label="Geo" options={facets.geo} selected={geo} onToggle={toggle(setGeo, geo)} />
      </div>

      <div className="text-xs text-neutral-500">
        Showing {results.length} fund{results.length === 1 ? '' : 's'}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {results.map((fund) => (
          <Link
            key={fund.slug}
            to={`/admin/investors/funds/${fund.slug}`}
            className="group flex flex-col gap-2 rounded-lg border border-neutral-200 bg-white p-4 hover:border-neutral-400 hover:shadow-sm transition-all"
          >
            <div className="flex items-start gap-3">
              {fund.logoUrl ? (
                <img
                  src={fund.logoUrl}
                  alt={fund.name}
                  className="h-12 w-12 rounded object-contain bg-white border border-neutral-200 p-1"
                  loading="lazy"
                />
              ) : (
                <div className="h-12 w-12 rounded bg-neutral-100 flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5 text-neutral-400" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-neutral-900 truncate group-hover:text-neutral-700">
                  {fund.name.trim()}
                </div>
                <div className="text-xs text-neutral-500 truncate">
                  {fund.partnerCount} partner{fund.partnerCount === 1 ? '' : 's'}
                  {fund.location && <span className="text-neutral-400"> · {fund.location}</span>}
                </div>
              </div>
            </div>

            <div className="text-xs text-neutral-600">
              {fund.fundSizeText && <span className="font-medium">{fund.fundSizeText}</span>}
              {fund.fundSizeText && fund.stages.length > 0 && <span className="text-neutral-400"> · </span>}
              {fund.stages.length > 0 && <span>{fund.stages.slice(0, 3).join(', ')}</span>}
            </div>

            {fund.sectors.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {fund.sectors.slice(0, 3).map((s) => (
                  <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-neutral-100 text-neutral-600">
                    {s}
                  </span>
                ))}
                {fund.sectors.length > 3 && (
                  <span className="px-1.5 py-0.5 text-[10px] text-neutral-400">+{fund.sectors.length - 3}</span>
                )}
              </div>
            )}
          </Link>
        ))}
      </div>

      {results.length === 0 && status !== 'LoadingMore' && (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-500">
          No funds match the current filters.
        </div>
      )}

      {status === 'CanLoadMore' && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={() => loadMore(60)}
            className="text-xs px-4 py-2 rounded-md border border-neutral-300 hover:border-neutral-500 text-neutral-700"
          >
            Load more
          </button>
        </div>
      )}
      {status === 'LoadingMore' && (
        <div className="flex justify-center pt-2">
          <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
        </div>
      )}
    </div>
  )
}

export default function AdminInvestorsPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const [tab, setTab] = useState<Tab>('people')
  const [search, setSearch] = useState('')

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Investors</h1>
          <p className="text-sm text-neutral-500 mt-0.5">
            Reconstructed from vcsheet.com.{' '}
            <a
              href="https://www.vcsheet.com/investors"
              target="_blank"
              rel="noreferrer noopener"
              className="text-neutral-500 hover:text-neutral-700 inline-flex items-center gap-1"
            >
              source <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
          <input
            type="text"
            placeholder={tab === 'people' ? 'Search people…' : 'Search funds…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-sm border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-500 w-64"
          />
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-neutral-200">
        <button
          type="button"
          onClick={() => setTab('people')}
          className={cn(
            'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            tab === 'people'
              ? 'border-neutral-900 text-neutral-900'
              : 'border-transparent text-neutral-500 hover:text-neutral-700',
          )}
        >
          People
        </button>
        <button
          type="button"
          onClick={() => setTab('funds')}
          className={cn(
            'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            tab === 'funds'
              ? 'border-neutral-900 text-neutral-900'
              : 'border-transparent text-neutral-500 hover:text-neutral-700',
          )}
        >
          Funds
        </button>
      </div>

      {tab === 'people' ? <PeopleTab search={search} /> : <FundsTab search={search} />}
    </div>
  )
}
